import React, { useState } from 'react';
import { AuditLogFilters, AuditSeverity } from '../../../types/audit';

interface Props {
  filters: AuditLogFilters;
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  onExport: (format: 'csv' | 'json') => void;
}

export function AuditLogFiltersPanel({ 
  filters, 
  onFiltersChange,
  onExport 
}: Props) {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleReset = () => {
    const resetFilters: AuditLogFilters = {
      page: 1,
      pageSize: 50,
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow mb-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Filtros</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Rango de fechas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha desde
          </label>
          <input
            type="datetime-local"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.fromDate?.slice(0, 16) || ''}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              fromDate: e.target.value ? new Date(e.target.value).toISOString() : undefined
            })}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha hasta
          </label>
          <input
            type="datetime-local"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.toDate?.slice(0, 16) || ''}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              toDate: e.target.value ? new Date(e.target.value).toISOString() : undefined
            })}
          />
        </div>

        {/* Módulo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Módulo
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.module || ''}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              module: e.target.value || undefined
            })}
          >
            <option value="">Todos</option>
            <option value="Authentication">Autenticación</option>
            <option value="WorkOrders">Órdenes de Trabajo</option>
            <option value="Customers">Clientes</option>
            <option value="Items">Artículos</option>
            <option value="Farmers">Agricultores</option>
            <option value="Production">Producción</option>
            <option value="Printing">Impresión</option>
            <option value="Auditing">Auditoría</option>
          </select>
        </div>

        {/* Severidad */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Severidad
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.severity || ''}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              severity: e.target.value as AuditSeverity || undefined
            })}
          >
            <option value="">Todas</option>
            <option value="Info">Información</option>
            <option value="Warning">Advertencia</option>
            <option value="Error">Error</option>
            <option value="Critical">Crítico</option>
          </select>
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estado
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.isSuccess === undefined ? '' : String(localFilters.isSuccess)}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              isSuccess: e.target.value === '' ? undefined : e.target.value === 'true'
            })}
          >
            <option value="">Todos</option>
            <option value="true">Exitoso</option>
            <option value="false">Fallido</option>
          </select>
        </div>

        {/* Búsqueda */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Búsqueda
          </label>
          <input
            type="text"
            placeholder="Buscar en descripción..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            value={localFilters.search || ''}
            onChange={(e) => setLocalFilters({
              ...localFilters,
              search: e.target.value || undefined
            })}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleApplyFilters}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
        >
          Aplicar Filtros
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
        >
          Limpiar
        </button>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => onExport('csv')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Exportar CSV
          </button>
          <button
            onClick={() => onExport('json')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Exportar JSON
          </button>
        </div>
      </div>
    </div>
  );
}
